import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	Button, CircularProgress, Paper, Typography,
} from "@material-ui/core";
import requests from "../../../Services/HttpServices";
import "./CameraArdenteAuthorization.css";

const readPayload = (response) => response?.data?.data || response?.data || null;

export default function CameraArdenteAuthorization({ location }) {
	const token = useMemo(() => new URLSearchParams(location.search).get("token") || "", [location.search]);
	const [loading, setLoading] = useState(true);
	const [deciding, setDeciding] = useState(false);
	const [summary, setSummary] = useState(null);
	const [error, setError] = useState("");
	const [result, setResult] = useState("");

	const loginUrl = useMemo(() => {
		const returnTo = `${location.pathname}${location.search}`;
		return `/login?returnTo=${encodeURIComponent(returnTo)}`;
	}, [location.pathname, location.search]);

	const loadAuthorization = useCallback(() => {
		if (!/^[a-f0-9]{64}$/i.test(token)) {
			setError("Il link di autorizzazione non è valido.");
			setLoading(false);
			return;
		}

		setLoading(true);
		requests("get", "camereArdentiAuthorization", `?token=${encodeURIComponent(token)}`).then((response) => {
			const payload = readPayload(response);
			if (response?.data?.response === "success" && payload) {
				setSummary(payload);
				setError("");
				return;
			}

			const code = payload?.errorCode || response?.data?.errorCode;
			if (code === "AUTH_REQUIRED") {
				window.location.assign(loginUrl);
				return;
			}
			setError(payload?.message || response?.data?.message || "Non è possibile aprire questa richiesta.");
		}).catch(() => {
			setError("Non è stato possibile caricare la richiesta.");
		}).finally(() => setLoading(false));
	}, [loginUrl, token]);

	useEffect(() => {
		loadAuthorization();
	}, [loadAuthorization]);

	const loginWithAnotherAccount = () => {
		requests("get", "logout").finally(() => {
			sessionStorage.removeItem("user");
			sessionStorage.removeItem("User");
			sessionStorage.removeItem("UserAddress");
			sessionStorage.removeItem("ExpiringMemorial");
			window.location.assign(loginUrl);
		});
	};

	const decide = (decision) => {
		setDeciding(true);
		setError("");
		requests("post", "camereArdentiDecision", { token, decision }).then((response) => {
			const payload = response?.data || response;
			if (response?.response === "success") {
				let resultMessage = decision === "accepted" ? "Richiesta accettata." : "Richiesta rifiutata.";
				if (payload?.notificationSent === false) {
					resultMessage += " Non è stato possibile inviare la notifica al partner richiedente.";
				}
				setResult(resultMessage);
				setSummary(null);
				return;
			}
			if (response?.errorCode === "AUTH_REQUIRED") {
				window.location.assign(loginUrl);
				return;
			}
			setError(response?.message || payload?.message || "Non è stato possibile registrare la decisione.");
		}).catch(() => setError("Non è stato possibile registrare la decisione."))
			.finally(() => setDeciding(false));
	};

	return (
		<div className="camera-authorization-page">
			<Paper className="camera-authorization-card" elevation={3}>
				<Typography variant="h4" component="h1" className="camera-authorization-title">
					Autorizzazione Camera Ardente
				</Typography>

				{loading && (
					<div className="camera-authorization-loading">
						<CircularProgress />
					</div>
				)}

				{!loading && summary && (
					<>
						<Typography className="camera-authorization-intro">
							Il partner <strong>{summary.requester?.name}</strong> richiede il permesso di collocare
							 il defunto <strong>{summary.deceased?.name} {summary.deceased?.surname}</strong> nella tua casa funeraria.
						</Typography>
						<div className="camera-authorization-summary">
							<div><span>Casa funeraria</span><strong>{summary.owner?.name}</strong></div>
							<div><span>Sala</span><strong>{summary.salaLabel}</strong></div>
						</div>
						<Typography className="camera-authorization-warning">
							{summary.roomAvailable
								? "Accettando, l’eventuale defunto attualmente associato a questa sala verrà sostituito."
								: "La sala richiesta non è più disponibile. Puoi soltanto rifiutare la richiesta."}
						</Typography>
						<div className="camera-authorization-actions">
							<Button
								variant="contained"
								color="primary"
								disabled={deciding || !summary.roomAvailable}
								onClick={() => decide("accepted")}
							>
								Accetta
							</Button>
							<Button
								variant="outlined"
								color="secondary"
								disabled={deciding}
								onClick={() => decide("rejected")}
							>
								Rifiuta
							</Button>
							{deciding && <CircularProgress size={24} />}
						</div>
					</>
				)}

				{!loading && error && (
					<>
						<Typography className="camera-authorization-error">{error}</Typography>
						{(sessionStorage.getItem("User") || sessionStorage.getItem("user")) && (
							<Button variant="outlined" onClick={loginWithAnotherAccount}>
								Accedi con l’account corretto
							</Button>
						)}
					</>
				)}

				{!loading && result && (
					<Typography className="camera-authorization-result">{result}</Typography>
				)}
			</Paper>
		</div>
	);
}